<template>
  <div class="TableNav">
    <div v-if="total && total > 3" class="page-meta">
      {{ $t('outOf', [pageStart, pageEnd, total]) }}
    </div>
    <template v-if="totalPagesCount > 1">
      <template v-for="(page, index) in pagesNumerics">
        <div v-if="!page" :key="`x${index}`" class="dots">
          . . .
        </div>
        <div
          v-else
          :key="page"
          class="one"
          :class="{ active: (page - 1) === table.page }"
          @click="setTo(page - 1)"
          @keydown.enter="setTo(page - 1)">
          {{ page }}
        </div>
      </template>
    </template>
    <div
      v-if="totalPagesCount > 1 || table.perPage !== 12"
      class="per-page">
      <Field
        :options="perPageOptions"
        type="select"
        :value="table.perPage"
        @update:value="PATCH_TABLE({ table, perPage: $event })" />
    </div>
  </div>
</template>

<script>
import { actions } from '../../utils'

// An example in procedural style:
// https://gist.github.com/kottenator/9d936eb3e4e3c3e02598#file-simple-pagination-js-L3
export default {
  props: {
    table: Object,
    total: Number,
  },
  computed: {
    // @source https://gist.github.com/kottenator/9d936eb3e4e3c3e02598#gistcomment-2899014
    pagesNumerics() {
      const currentPage = this.table.page + 1
      const pageCount = this.totalPagesCount
      const separate = (a, b) => ({
        0: [a],
        1: [a, b],
        2: [a, a + 1, b],
      }[b - a] || [a, null, b])

      return new Array(3)
        .fill()
        .map((smth, index) => currentPage - 1 + index)
        .filter(page => page > 0 && page <= pageCount)
        .flatMap((page, index, { length }) => {
          if (!index) return separate(1, page)
          if (index === length - 1) return separate(page, pageCount)
          return [page]
        })
    },
    perPageOptions() {
      return [12, 50, 100, 200].map(perPage => [perPage, this.$t('perPage', [perPage])])
    },
    skipped() {
      return this.table.perPage * this.table.page
    },
    pageStart() {
      return this.skipped + 1
    },
    pageEnd() {
      return Math.min(this.skipped + this.table.perPage, this.total)
    },
    totalPagesCount() {
      return Math.ceil(this.total / this.table.perPage)
    },
  },
  methods: {
    setTo(page) {
      this.$emit('change', page)
    },
    ...actions('PATCH_TABLE'),
  },
}
</script>

<style lang="scss">
.TableNav {
  padding: 0 1.5rem 2rem;
  text-align: center;

  > .page-meta {
    float: left;
  }

  > .per-page {
    float: right;
  }

  > .dots {
    display: inline-block;
    font-size: $h3;
    transform: translateY(-0.125rem);
  }

  > .one {
    background: $white;
    border: 1px solid $border-color;
    border-radius: $radius;
    cursor: pointer;
    display: inline-block;
    margin: 0 0.5rem;
    padding: 0.5rem 1rem;

    &:hover {
      background: $grey-lightest;
    }

    &.active {
      background: $success;
      border-color: $success;
      color: $white;
    }
  }
}
</style>

<i18n>
{
  "en": {
    "outOf": "{0} - {1} ouf of {2} records",
    "perPage": "{0} per page"
  }
}
</i18n>
